'use client';

import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { cn } from '@kit/ui/utils';

/** One roster entry from get_class_participants_public. */
export interface ClassPlayer {
  name: string;
  pictureUrl: string | null;
}

/** How many names to show before collapsing the rest into "and N others". */
const PREVIEW_COUNT = 2;

function initialsOf(name: string) {
  const words = name.trim().split(/\s+/);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

function PlayerAvatar({
  player,
  className,
}: {
  player: ClassPlayer;
  className?: string;
}) {
  return (
    <Avatar className={cn('h-7 w-7', className)}>
      {player.pictureUrl && (
        <AvatarImage src={player.pictureUrl} alt={player.name} />
      )}
      <AvatarFallback className="text-[10px] font-medium">
        {initialsOf(player.name)}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Compact overlapping avatar row (images or initials) with a "+N" bubble for
 * the overflow — the at-a-glance form of the player list on the class card.
 */
export function PlayerAvatarStack({
  players,
  max = 3,
  className,
}: {
  players: ClassPlayer[];
  max?: number;
  className?: string;
}) {
  if (players.length === 0) {
    return null;
  }

  const visible = players.slice(0, max);
  const overflow = players.length - visible.length;

  return (
    <div className={cn('flex items-center', className)}>
      {visible.map((player, index) => (
        <PlayerAvatar
          key={`${player.name}-${index}`}
          player={player}
          className={cn('ring-background ring-2', index > 0 && '-ms-2')}
        />
      ))}
      {overflow > 0 && (
        <Avatar className="ring-background -ms-2 h-7 w-7 ring-2">
          <AvatarFallback className="bg-muted text-muted-foreground text-[10px] font-medium">
            +{overflow}
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  );
}

/**
 * Player list for a class session: avatar stack + "Elie Jalkh, Kareem Jaroudi
 * and 3 others", where the "others" part expands the full list in place.
 * Inline expansion (not a dialog) so it behaves identically inside the /book
 * details drawer and the /event info card without stacking overlays on mobile.
 *
 * Renders nothing for an empty list — the caller doesn't need to gate on the
 * per-template show_participants flag because get_class_participants_public
 * already fails closed to zero rows when it is off.
 */
export function ParticipantsPreview({ players }: { players: ClassPlayer[] }) {
  const { t } = useTranslation('bookings');
  const [expanded, setExpanded] = useState(false);

  if (players.length === 0) {
    return null;
  }

  const hiddenCount = players.length - PREVIEW_COUNT;
  const showToggle = hiddenCount > 0;

  if (expanded) {
    return (
      <div className="space-y-2">
        {players.map((player, index) => (
          <div
            key={`${player.name}-${index}`}
            className="flex items-center gap-2"
          >
            <PlayerAvatar player={player} />
            <span>{player.name}</span>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-primary underline underline-offset-2"
        >
          {t('playerList.showLess')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <PlayerAvatarStack players={players} />
      <p>
        {players
          .slice(0, PREVIEW_COUNT)
          .map((player) => player.name)
          .join(', ')}
        {showToggle && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-primary underline underline-offset-2"
            >
              {t('playerList.andOthers', { count: hiddenCount })}
            </button>
          </>
        )}
      </p>
    </div>
  );
}
